/**
 * Phase 6-γ.3 — CRM Connector Gallery (replaces the Integrations placeholder).
 *
 * Three sections:
 *   1. "Verbunden" — current tenant connections from GET /integrations/crm/connections
 *   2. "Verfügbare Konnektoren" — connectors gallery from GET /integrations/crm/connectors
 *      - viona: green badge + Manage CTA
 *      - custom_webhook: yellow Partial badge + Configure CTA
 *      - placeholders: grey Coming Soon + Notify Me modal → POST /notify-interest
 */
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Plug, BellRing, CheckCircle2, AlertTriangle, Sparkles } from "lucide-react";
import { api } from "../../lib/api";
import { useT } from "../../lib/i18n";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader,
  DialogTitle, DialogTrigger,
} from "../../components/ui/dialog";
import { Textarea } from "../../components/ui/textarea";
import { toast } from "sonner";

const STATUS_PRESET = {
  mock: { tone: "bg-emerald-100 text-emerald-700", icon: CheckCircle2 },
  partial: { tone: "bg-amber-100 text-amber-700", icon: AlertTriangle },
  not_yet_implemented: { tone: "bg-muted text-muted-foreground", icon: BellRing },
  active: { tone: "bg-emerald-100 text-emerald-700", icon: CheckCircle2 },
};


function NotifyDialog({ kind, displayName, onSubmitted }) {
  const t = useT();
  const [open, setOpen] = useState(false);
  const [message, setMessage] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const submit = async () => {
    setSubmitting(true);
    try {
      const r = await api.post("/integrations/crm/notify-interest", {
        connector_kind: kind, message: message || undefined,
      });
      toast.success(t("gallery.notify_success", `Danke! Wir melden uns sobald ${displayName} verfügbar ist.`));
      setOpen(false); setMessage("");
      onSubmitted && onSubmitted(r.data);
    } catch (e) {
      toast.error(t("gallery.notify_error", "Konnte deine Interesse-Meldung nicht speichern."));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" size="sm"
                  data-testid={`gallery-notify-btn-${kind}`}>
          <BellRing className="h-4 w-4 mr-2" />
          {t("gallery.notify_me", "Benachrichtigen")}
        </Button>
      </DialogTrigger>
      <DialogContent data-testid={`gallery-notify-modal-${kind}`}>
        <DialogHeader>
          <DialogTitle>{displayName}</DialogTitle>
          <DialogDescription>
            {t("gallery.notify_desc",
                  "Wir benachrichtigen dich, sobald dieser Konnektor live ist. Beschreibe optional deinen Use Case.")}
          </DialogDescription>
        </DialogHeader>
        <Textarea
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          rows={3}
          placeholder={t("gallery.notify_placeholder",
                          "Wie würdest du diesen Konnektor nutzen? (optional)")}
          data-testid={`gallery-notify-textarea-${kind}`}
        />
        <DialogFooter>
          <Button variant="ghost" onClick={() => setOpen(false)}>
            {t("common.cancel", "Abbrechen")}
          </Button>
          <Button onClick={submit} disabled={submitting}
                    data-testid={`gallery-notify-submit-${kind}`}>
            {submitting ? t("common.saving", "Speichern…") : t("gallery.notify_submit", "Senden")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


function ConnectorCard({ connector, connected, t }) {
  const navigate = useNavigate();
  const status = connector.implementation_status;
  const preset = STATUS_PRESET[status] || STATUS_PRESET.not_yet_implemented;
  const Icon = preset.icon;
  const StatusBadge = (
    <Badge className={preset.tone} data-testid={`gallery-status-${connector.kind}`}>
      <Icon className="h-3 w-3 mr-1 inline" />
      {status === "mock" || status === "active"
        ? t("gallery.status_connected", "Verbunden")
        : status === "partial"
          ? t("gallery.status_partial", "Teilweise verfügbar")
          : t("gallery.status_coming_soon", "Bald verfügbar")}
    </Badge>
  );

  return (
    <Card data-testid={`gallery-card-${connector.kind}`}
            className={status === "not_yet_implemented" ? "opacity-75" : ""}>
      <CardContent className="p-5 space-y-3">
        <div className="flex items-start justify-between">
          <div>
            <div className="text-lg font-semibold">{connector.display_name}</div>
            <div className="text-xs text-muted-foreground mt-1">
              {connector.kind}
            </div>
          </div>
          <Plug className="h-5 w-5 text-muted-foreground" />
        </div>
        <div>{StatusBadge}</div>
        <div className="pt-2">
          {status === "mock" || status === "active" ? (
            <Button size="sm" variant="outline"
                      onClick={() => connected && navigate(
                        `/app/integrations/crm/connections/${connected.id}`)}
                      disabled={!connected}
                      data-testid={`gallery-manage-btn-${connector.kind}`}>
              {t("gallery.manage_cta", "Verwalten")}
            </Button>
          ) : status === "partial" ? (
            <Button size="sm" variant="outline"
                      data-testid={`gallery-configure-btn-${connector.kind}`}>
              {t("gallery.configure_cta", "Konfigurieren")}
            </Button>
          ) : (
            <NotifyDialog kind={connector.kind}
                              displayName={connector.display_name} />
          )}
        </div>
      </CardContent>
    </Card>
  );
}


export default function Integrations() {
  const t = useT();
  const [connectors, setConnectors] = useState(null);
  const [connections, setConnections] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      api.get("/integrations/crm/connectors"),
      api.get("/integrations/crm/connections"),
    ])
      .then(([cReq, conReq]) => {
        setConnectors(cReq.data?.items || []);
        setConnections(conReq.data?.items || []);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  if (loading) return (
    <div className="p-6 text-sm text-muted-foreground" data-testid="integrations-loading">
      {t("integrations.loading", "Lade Integrationen…")}
    </div>
  );

  return (
    <div className="p-6 space-y-8" data-testid="integrations-page">
      <header>
        <h1 className="text-3xl font-bold tracking-tight inline-flex items-center gap-2">
          <Plug className="h-7 w-7" />
          {t("integrations.title", "Integrationen")}
        </h1>
        <p className="text-sm text-muted-foreground mt-1">
          {t("integrations.subtitle",
                "Verbinde dein CRM, deine Ad-Plattformen und Notification-Channels.")}
        </p>
      </header>

      {/* Connected section */}
      <section data-testid="integrations-connected-section">
        <h2 className="text-lg font-semibold mb-3">
          {t("integrations.connected_title", "Verbunden")}
          <span className="text-sm text-muted-foreground ml-2">
            ({connections.length})
          </span>
        </h2>
        {connections.length === 0 ? (
          <div className="text-sm text-muted-foreground"
                  data-testid="integrations-connected-empty">
            {t("integrations.no_connections",
                  "Noch keine Verbindungen — wähle unten einen Konnektor.")}
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            {connections.map((c) => (
              <Card key={c.id} data-testid={`integrations-conn-${c.id}`}>
                <CardContent className="p-4 space-y-1">
                  <div className="font-medium">{c.name}</div>
                  <div className="text-xs text-muted-foreground">
                    {c.connector_kind} · {c.status}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </section>

      {/* Available gallery */}
      <section data-testid="integrations-available-section">
        <h2 className="text-lg font-semibold mb-3 inline-flex items-center gap-2">
          <Sparkles className="h-5 w-5" />
          {t("integrations.available_title", "Verfügbare Konnektoren")}
        </h2>
        <p className="text-sm text-muted-foreground mb-4">
          {t("integrations.available_subtitle",
                "Wähle einen Konnektor. Für noch nicht verfügbare CRMs kannst du dich benachrichtigen lassen.")}
        </p>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
          {(connectors || []).map((c) => (
            <ConnectorCard key={c.kind} connector={c} t={t} />
          ))}
        </div>
      </section>
    </div>
  );
}
